/*
 * DATE wizard input component
 * <p>
 *
 * @author lachezar.parvin
 * @author Gallagher Bassett IT
 * @author Copyright (c) 2020 Gallagher Bassett Services, Inc.
 *
 */

import {
  AfterViewInit,
  Component,
  ElementRef,
  forwardRef,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild
} from '@angular/core';

import {
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator
} from '@angular/forms';
import { DatePickerComponent, ChangedEventArgs } from '@syncfusion/ej2-angular-calendars';

@Component ( {
    selector: 'reporter-date',
    templateUrl: './date.component.html',
    styleUrls: ['./date.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef ( () => DateComponent ),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef ( () => DateComponent ),
            multi: true
        },
    ],
    standalone: false
} )

export class DateComponent implements ControlValueAccessor, Validator, AfterViewInit, OnChanges {

  @Input () futureOk = false;
  @Input () masked = false;
  @Input () minDate: Date | string | null = null;
  @Input () maxDate: Date | string | null = null;


  effectiveMaxDate: Date;
  effectiveMinDate: Date | null = null;

  sfValue: Date | null = null;

  maskPlaceholder = { day: 'dd', month: 'MM', year: 'yyyy' };

  innerValue: any;
  initialized = false;
  initialValue: any;
  maskedValue: string | null = null;

  @ViewChild ( 'sfPicker' ) sfPicker: DatePickerComponent;

  onChange = ( _: any ) => {};
  onTouched = () => {};

  constructor ( private elementRef: ElementRef ) {
    const tomorrow = new Date ();
    tomorrow.setDate ( tomorrow.getDate () + 1 );
    this.effectiveMaxDate = tomorrow;
  }

  set value( v: any ) {
    if ( v !== this.innerValue ) {
      this.innerValue = v;
      this.onChange ( v );
    }
  }

  get value() {
    return this.innerValue;
  }

  get required (): boolean {
    return this.elementRef.nativeElement.attributes.hasOwnProperty ( 'required' );
  }

  ngOnChanges ( changes: SimpleChanges ): void {
    if ( changes[ 'maxDate' ] || changes[ 'futureOk' ] ) {
      this.updateEffectiveMaxDate ();
    }
    if ( changes[ 'minDate' ] ) {
      this.effectiveMinDate = this.toDate ( this.minDate );
    }
  }

  ngAfterViewInit () {
    this.initialValue = this.value;
    this.maskedValue = this.masked ? '**/**/****' : null;
    this.initialized = true;
  }

  writeValue ( value: any ): void {
    this.innerValue = value;
    if ( value && /\d{4}-\d{2}-\d{2}/.test ( value ) ) {
      const [year, month, day] = ( value as string ).split ( '-' ).map ( Number );
      this.sfValue = new Date ( year, month - 1, day );
    } else {
      this.sfValue = null;
    }
    this.maskedValue = null;
  }

  registerOnChange ( fn: ( value: any ) => any ): void {
    this.onChange = fn;
  }

  registerOnTouched ( fn: () => any ): void {
    this.onTouched = fn;
  }

  setDisabledState ( isDisabled: boolean ): void {
    if ( this.sfPicker ) {
      this.sfPicker.enabled = !isDisabled;
    }
  }


  validate ( control: FormControl ): ValidationErrors | null {
    if ( !this.initialized ) {
      return null;
    }
    if ( this.required && !control.value ) {
      return { required: true, message: ' is required.' };
    }
    if ( control.value && control.value !== this.initialValue ) {
      const date = new Date ( control.value );
      if ( !( date instanceof Date && isFinite ( date.getTime () ) ) ) {
        return { badInput: true, message: ' must be a correct date.' };
      }
      if ( !this.futureOk ) {
        const tomorrow = new Date ();
        tomorrow.setDate ( tomorrow.getDate () + 1 );
        if ( date > tomorrow ) {
          return { max: true, message: ' cannot be a future date.' };
        }
      }
      if ( !/^\d{4}-\d{2}-\d{2}$/.test ( control.value ) ) {
        return { badInput: true, message: ' must be a correct date.' };
      }
    }
    return null;
  }


  onSfChange ( event: ChangedEventArgs ): void {
    const iso = event.value ? this.toIsoString ( event.value ) : '';
    this.onChange ( iso );
    this.onTouched ();
  }


  unmask (): void {
    this.masked = false;
    this.maskedValue = null;
    setTimeout ( () => {
      if ( this.sfPicker ) {
        this.sfPicker.focusIn ();
      }
    }, 0 );
  }

  maskedFocus ( $event: any ): void {
    $event.target.select ();
  }


  private updateEffectiveMaxDate (): void {
    if ( this.maxDate ) {
      this.effectiveMaxDate = this.toDate ( this.maxDate );
    } else if ( !this.futureOk ) {
      const tomorrow = new Date ();
      tomorrow.setDate ( tomorrow.getDate () + 1 );
      this.effectiveMaxDate = tomorrow;
    } else {
      this.effectiveMaxDate = null;
    }
  }

  private toDate ( value: Date | string | null ): Date | null {
    if ( !value ) {
      return null;
    }
    if ( value instanceof Date ) {
      return value;
    }
    if ( typeof value === 'string' && /\d{4}-\d{2}-\d{2}/.test ( value ) ) {
      const [year, month, day] = value.split ( '-' ).map ( Number );
      return new Date ( year, month - 1, day );
    }
    return null;
  }

  private toIsoString ( date: Date ): string {
    const y = date.getFullYear ();
    const m = String ( date.getMonth () + 1 ).padStart ( 2, '0' );
    const d = String ( date.getDate () ).padStart ( 2, '0' );
    return `${y}-${m}-${d}`;
  }
}
